/**
 * My Chase Cards: the ranked ten, five across and two down.
 *
 * Always ten cells. An empty square is a (+) tile rather than nothing, because
 * the room left should be visible without counting, and the rank is printed on
 * every square so the order is a fact rather than a guess about the layout.
 * The slot a card holds comes from the database - see topTenSlots.
 *
 * A tap opens the card. A hold opens the bottom menu, through the hold that is
 * cancelled by scrolling rather than by distance.
 *
 * WHERE THE ARTWORK COMES FROM, AND WHY IT USED TO COME FROM NOWHERE.
 *
 * `art` is a map of card id to catalogue image URL, resolved by whoever renders
 * this grid. These tiles used to build their own src out of
 * `wish_list.image_url`, and the add path never wrote that column, so every row
 * in the wild carried null and every tile rendered the "card found, no image"
 * panel instead of a card. The stored value is kept only as a fallback: card art
 * in this app is self-hosted and gets re-mirrored, so a URL frozen into a wish
 * row at the moment somebody tapped Add is the wrong thing to trust.
 *
 * Nothing here appends a size or an extension either. CardImage owns that - it
 * asks the storage render endpoint for a tile-sized copy and falls back through
 * the original and the second catalogue - and a src hand-built out here is a
 * second, worse copy of that chain.
 */

import { useRef } from 'react';
import { Plus } from 'lucide-react';
import CardImage from '../CardImage';
import { holdProps } from './wishHold';
import { buildTopTenSlots } from './topTenSlots';
import { TOP_TEN_LIMIT } from '../../services/wishList';

export default function TopTenGrid({
  items,
  theme,
  art = {},
  artPending = false,
  pricingEnabled = false,
  prices = {},
  readOnly = false,
  onOpenCard,
  onHoldCard,
  onAdd,
}) {
  const slots = buildTopTenSlots(items, TOP_TEN_LIMIT);
  // Shared with the tap handler so the tap iOS sends at the end of a hold does
  // not also open the card behind the menu.
  const heldRef = useRef(false);

  return (
    <div style={styles.grid} data-testid="top-ten-grid">
      {slots.map(({ position, card }) => {
        if (!card) {
          // Someone else's ten shows its gaps as gaps. Only the owner is
          // invited to fill them.
          if (readOnly) {
            return (
              <div key={`empty-${position}`} style={{ ...styles.slot, borderColor: theme.border }}>
                <span style={{ ...styles.rank, color: theme.textSecondary }}>{position}</span>
              </div>
            );
          }
          return (
            <button
              key={`add-${position}`}
              type="button"
              style={{ ...styles.slot, ...styles.addSlot, borderColor: theme.border, backgroundColor: theme.bgCard }}
              onClick={() => onAdd?.(position)}
              aria-label={`Add a chase card to slot ${position}`}
              data-testid={`top-ten-add-${position}`}
            >
              <Plus size={18} color={theme.textMuted} />
              <span style={{ ...styles.rank, color: theme.textSecondary }}>{position}</span>
            </button>
          );
        }

        const priced = prices[card.tcgdex_card_id];
        return (
          <div
            key={card.id}
            style={{ ...styles.slot, borderColor: theme.border }}
            onClick={() => {
              if (heldRef.current) return;
              onOpenCard?.(card);
            }}
            data-testid={`top-ten-card-${position}`}
            {...holdProps({
              onHold: () => onHoldCard?.(card),
              firedRef: heldRef,
              disabled: readOnly,
            })}
            onContextMenu={(e) => {
              e.preventDefault();
              if (!readOnly) onHoldCard?.(card);
            }}
          >
            <CardImage
              digitalImage={art[card.tcgdex_card_id] || card.image_url || null}
              name={card.card_name}
              number={(card.tcgdex_card_id || '').replace(`${card.set_id}-`, '')}
              setName={card.set_name}
              theme={theme}
              style={styles.art}
              imgStyle={styles.artImg}
              // The ten is the first thing on the chase tab and is on screen at
              // first paint, so deferring it to an intersection callback only
              // delays it. Everything below the fold stays lazy.
              loading="eager"
              thumb
              // Only while the lookup is out, and only for a card whose URL has
              // not arrived. A row that carries its own stored image_url is not
              // waiting on anything.
              pending={artPending && !art[card.tcgdex_card_id] && !card.image_url}
            />
            <span style={{ ...styles.rankBadge, color: '#fff' }}>{position}</span>
            {/* Cost belongs at every level, so the price rides the tile the
                same way it rides a collection card. */}
            {pricingEnabled && priced?.formatted && (
              <span style={{ ...styles.price, color: '#4ade80' }}>{priced.formatted}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  grid: {
    display: 'grid',
    // Five across, two down, at any phone width. The cells are square-ish
    // card aspect, so the row height follows the width rather than fighting it.
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '6px',
  },
  slot: {
    position: 'relative',
    aspectRatio: '5 / 7',
    borderRadius: '8px',
    border: '1px solid',
    overflow: 'hidden',
    cursor: 'pointer',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  addSlot: {
    // An empty square reads as an invitation rather than as a card that failed
    // to load, which is what the dashed edge is doing here.
    border: '1px dashed',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    outline: 'none',
  },
  art: { width: '100%', height: '100%' },
  artImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  rank: { fontSize: '10px', fontWeight: '700' },
  rankBadge: {
    position: 'absolute',
    top: '3px',
    left: '3px',
    minWidth: '16px',
    padding: '1px 4px',
    borderRadius: '6px',
    backgroundColor: 'rgba(0, 0, 0, 0.72)',
    fontSize: '10px',
    fontWeight: '700',
    textAlign: 'center',
  },
  price: {
    position: 'absolute',
    bottom: '3px',
    right: '3px',
    padding: '1px 4px',
    borderRadius: '6px',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    fontSize: '9px',
    fontWeight: '700',
  },
};
